// Brands tab: the guided brand-provisioning WORKFLOW. Brand provisioning is a
// human-supervised, must-complete sequence with an offline step in the middle:
//
//   1. Create   → server allocates the identity + returns the coc-mint command
//   2. Ceremony → operator runs coc-mint on their OWN box (key stays private)
//   3. Activate → operator pastes the PUBLIC assertions → brand goes active
//   4. Deliver  → build a kit → download it (field engineer installs on site)
//
// The UI view-gates on capabilities implicitly (the API is the real enforcer):
// buttons call the API and surface 403/404/501 as banners.
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  activateBrand,
  buildKit,
  deleteBrand,
  downloadKit,
  listBrands,
  listKits,
  mintBrand,
  type Brand,
  type CeremonyPack,
  type KitMeta,
} from "../api";
import { Banner } from "./Banner";

export function BrandsTab() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pack, setPack] = useState<CeremonyPack | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBrands(await listBrands());
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Stable callbacks so child effects (KitsPanel) don't see a new reference every
  // render — an unstable onError in a child dep array would storm the backend on
  // a persistent error.
  const notifyErr = useCallback((m: string) => {
    setError(m);
    setOk(null);
  }, []);
  const notifyChanged = useCallback(
    (m: string) => {
      setOk(m);
      setError(null);
      void refresh();
    },
    [refresh],
  );

  return (
    <section aria-labelledby="brands-heading">
      <h2 id="brands-heading" className="sr-only">
        Brands
      </h2>
      {error && <Banner kind="error">{error}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <CreateBrandForm
        onCreated={(p) => {
          setPack(p);
          setOk(`Brand "${p["display-name"]}" created. Run the ceremony below, then activate.`);
          setError(null);
          void refresh();
        }}
        onError={(m) => {
          setError(m);
          setOk(null);
        }}
      />

      {pack && <CeremonyPanel pack={pack} onDismiss={() => setPack(null)} />}

      <div className="card">
        <h2>Brands</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : brands.length === 0 ? (
          <p className="muted">No brands yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Namespace</th>
                <th>Tenant</th>
                <th>Status</th>
                <th>Brand ID</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <BrandRow
                  key={b["brand-id"]}
                  brand={b}
                  onChanged={notifyChanged}
                  onError={notifyErr}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function CreateBrandForm(props: {
  onCreated: (pack: CeremonyPack) => void;
  onError: (msg: string) => void;
}) {
  const [namespace, setNamespace] = useState("");
  const [tenant, setTenant] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const pack = await mintBrand({
        namespace,
        tenant,
        "display-name": displayName,
      });
      props.onCreated(pack);
      setNamespace("");
      setTenant("");
      setDisplayName("");
    } catch (err) {
      props.onError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>1 · Create a brand</h2>
      <p className="muted">
        Allocates the brand identity. No key is created here — you run the
        ceremony next, on your own box.
      </p>
      <form className="stack" onSubmit={submit}>
        <label>
          Namespace (reseller / BU)
          <input
            type="text"
            required
            autoComplete="off"
            placeholder="acme"
            value={namespace}
            onChange={(e) => setNamespace(e.target.value)}
          />
        </label>
        <label>
          Tenant (end customer)
          <input
            type="text"
            required
            autoComplete="off"
            placeholder="acme-corp"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
          />
        </label>
        <label>
          Display name
          <input
            type="text"
            required
            autoComplete="off"
            placeholder="Acme Corporation"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !namespace || !tenant || !displayName}
          >
            {busy ? "Creating…" : "Create brand"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CeremonyPanel(props: { pack: CeremonyPack; onDismiss: () => void }) {
  const { pack } = props;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pack.ceremony.command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable; the command is visible to copy manually
    }
  };
  return (
    <div className="card">
      <h2>2 · Run the ceremony (on your box)</h2>
      <p className="muted">{pack.ceremony.note}</p>
      <pre className="code-block">{pack.ceremony.command}</pre>
      <div>
        <button type="button" className="btn" onClick={() => void copy()}>
          {copied ? "Copied ✓" : "Copy command"}
        </button>{" "}
        <button type="button" className="btn" onClick={props.onDismiss}>
          Dismiss
        </button>
      </div>
      <p className="muted">
        Then find brand <code>{pack["brand-id"]}</code> in the table below and
        choose <strong>Activate</strong> to submit the public assertions.
      </p>
    </div>
  );
}

function BrandRow(props: {
  brand: Brand;
  onChanged: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const { brand } = props;
  const [expanded, setExpanded] = useState<"none" | "activate" | "kits">("none");
  const [deleting, setDeleting] = useState(false);
  const isPending = brand.status === "pending-ceremony";

  const del = async () => {
    if (
      !window.confirm(
        `Delete brand "${brand["display-name"]}" (${brand.tenant})? This cannot be undone.`,
      )
    )
      return;
    setDeleting(true);
    try {
      await deleteBrand(brand["brand-id"]);
      props.onChanged(`Brand "${brand["display-name"]}" deleted.`);
    } catch (err) {
      props.onError(messageOf(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <tr>
        <td>{brand.namespace}</td>
        <td>{brand.tenant}</td>
        <td>
          <StatusBadge brand={brand} />
        </td>
        <td>
          <code>{brand["brand-id"]}</code>
        </td>
        <td>
          {isPending ? (
            <button
              type="button"
              className="btn"
              onClick={() =>
                setExpanded(expanded === "activate" ? "none" : "activate")
              }
            >
              Activate
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              onClick={() => setExpanded(expanded === "kits" ? "none" : "kits")}
            >
              Kits
            </button>
          )}{" "}
          <button
            type="button"
            className="btn"
            onClick={() => void del()}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </td>
      </tr>
      {expanded === "activate" && (
        <tr>
          <td colSpan={5}>
            <ActivatePanel
              brand={brand}
              onDone={(m) => {
                setExpanded("none");
                props.onChanged(m);
              }}
              onError={props.onError}
            />
          </td>
        </tr>
      )}
      {expanded === "kits" && (
        <tr>
          <td colSpan={5}>
            <KitsPanel brand={brand} onError={props.onError} />
          </td>
        </tr>
      )}
    </>
  );
}

function StatusBadge({ brand }: { brand: Brand }) {
  if (brand.status === "active") {
    return (
      <span className="badge badge-ok">
        active
        {brand["chain-verified"] ? "" : " (chain unverified)"}
      </span>
    );
  }
  return <span className="badge badge-pending">pending ceremony</span>;
}

function ActivatePanel(props: {
  brand: Brand;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [assertions, setAssertions] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await activateBrand(props.brand["brand-id"], assertions);
      props.onDone(`Brand "${props.brand["display-name"]}" activated.`);
    } catch (err) {
      props.onError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <p className="muted">
        3 · Paste the <strong>public</strong> ceremony chain (the account +
        account-key assertions from <code>coc-mint</code>). Never paste a private
        key.
      </p>
      <textarea
        rows={8}
        className="mono"
        placeholder="type: account&#10;authority-id: …&#10;account-id: …&#10;…"
        value={assertions}
        onChange={(e) => setAssertions(e.target.value)}
      />
      <div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void submit()}
          disabled={busy || !assertions.trim()}
        >
          {busy ? "Activating…" : "Activate brand"}
        </button>
      </div>
    </div>
  );
}

function KitsPanel(props: { brand: Brand; onError: (msg: string) => void }) {
  const { brand } = props;
  // Depend on the stable brand-id string + the (now stable) onError callback —
  // NOT the whole `brand`/`props` objects, which change reference every parent
  // render and would re-fire the effect (a request storm on a persistent error).
  const brandID = brand["brand-id"];
  const onError = props.onError;
  const [kits, setKits] = useState<KitMeta[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "unconfigured">(
    "loading",
  );
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setState("loading");
    try {
      setKits(await listKits(brandID));
      setState("ready");
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setState("unconfigured");
      } else {
        onError(messageOf(e));
        setState("ready");
      }
    }
  }, [brandID, onError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const build = async () => {
    setBusy(true);
    try {
      await buildKit(brand["brand-id"], { domain, tls: "internal" });
      setDomain("");
      await refresh();
    } catch (err) {
      props.onError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (state === "unconfigured") {
    return (
      <p className="muted">
        Kit delivery is not configured on this instance (no signer).
      </p>
    );
  }

  return (
    <div className="stack">
      <p className="muted">4 · Build + hand over a deployment kit.</p>
      <div className="row">
        <input
          type="text"
          placeholder="store domain, e.g. store.acme.example.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
        />
        <button
          type="button"
          className="btn"
          onClick={() => void build()}
          disabled={busy || !domain}
        >
          {busy ? "Building…" : "Build kit"}
        </button>
      </div>
      {state === "loading" ? (
        <p className="muted">Loading kits…</p>
      ) : kits.length === 0 ? (
        <p className="muted">No kits yet.</p>
      ) : (
        <ul className="kit-list">
          {kits.map((k) => (
            <li key={k["kit-id"]}>
              <code>{k.version}</code> · {k["created-at"]}{" "}
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void downloadKit(k["kit-id"], `${k.tenant}-kit.tar`).catch(
                    (e) => props.onError(messageOf(e)),
                  )
                }
              >
                Download
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function messageOf(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error.";
}
